import { Routes } from '@angular/router';
import { RouterTools, solCanDeactivate } from '@sol/components';
import { AsmAuthority } from '@asm/components-core/models/asm-authority';
import { AsmRouteId } from '@asm/components-core/models/asm-route-id';

export const routesAdmin: Routes = [
  // Dashboard
  RouterTools.ofLazy(
    AsmRouteId.admin_dashboard,
    () =>
      import('@asm/components-platform-administration/components/admin/asm-admin-dashboard-page/asm-admin-dashboard-page.component').then(
        m => m.AsmAdminDashboardPageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_APP],
  ),

  // Partners
  RouterTools.ofLazy(
    AsmRouteId.admin_partners,
    () =>
      import('@asm/components-platform-administration/components/admin/asm-admin-partners-page/asm-admin-partners-page.component').then(
        m => m.AsmAdminPartnersPageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_PARTNERS],
  ),

  // Access management
  RouterTools.ofRedirect(AsmRouteId.admin_access_management, AsmRouteId.admin_users),
  RouterTools.ofLazy(
    AsmRouteId.admin_users,
    () =>
      import('@asm/components-access-management/components/access-management/asm-users-page/asm-users-page.component').then(
        m => m.AsmPartnerUsersPageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_USERS],
  ),
  RouterTools.ofLazy(
    AsmRouteId.admin_roles,
    () =>
      import('@asm/components-access-management/components/access-management/asm-roles-page/asm-roles-page.component').then(
        m => m.AsmPartnerRolesPageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_ROLES],
  ),
  RouterTools.ofRedirect(AsmRouteId.admin_api, AsmRouteId.admin_api_tokens),
  RouterTools.ofLazy(
    AsmRouteId.admin_api_tokens,
    () =>
      import('@asm/components-access-management/components/access-management/asm-api-tokens-page/asm-api-tokens-page.component').then(
        m => m.AsmPartnerApiTokensPageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_API],
  ),
  RouterTools.ofLazy(
    AsmRouteId.admin_api_docs,
    () =>
      import('@asm/components-access-management/components/access-management/asm-api-docs-page/asm-api-docs-page.component').then(
        m => m.AsmPartnerApiDocsPageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_API],
  ),

  // Derived access
  RouterTools.ofRedirect(AsmRouteId.admin_derived_access, AsmRouteId.admin_derived_roles),
  RouterTools.ofLazy(
    AsmRouteId.admin_derived_roles,
    () =>
      import('@asm/components-access-management/components/access-management/asm-admin-derived-roles-page/asm-admin-derived-roles-page.component').then(
        m => m.AsmAdminDerivedRolesPageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_ROLES],
  ),
  RouterTools.ofLazy(
    AsmRouteId.admin_access_reasons,
    () =>
      import('@asm/components-access-management/components/access-management/asm-admin-access-reasons-page/asm-admin-access-reasons-page.component').then(
        m => m.AsmAdminAccessReasonsPageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_USERS],
  ),

  // Usage — Overview (KPIs + consumption charts) and Events tabs render the same component.
  RouterTools.ofRedirect(AsmRouteId.admin_billing, AsmRouteId.admin_billing_overview),
  RouterTools.ofLazy(
    AsmRouteId.admin_billing_overview,
    () =>
      import('@asm/components-usage/components/shared/asm-usage-page/asm-usage-page.component').then(
        m => m.AsmUsagePageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_LICENSING, AsmAuthority.FEATURE_BILLING],
  ),
  RouterTools.ofLazy(
    AsmRouteId.admin_billing_events,
    () =>
      import('@asm/components-usage/components/shared/asm-usage-page/asm-usage-page.component').then(
        m => m.AsmUsagePageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_LICENSING, AsmAuthority.FEATURE_BILLING],
  ),

  // Internal
  RouterTools.ofRedirect(AsmRouteId.internal_drp_sources, AsmRouteId.internal_drp_sources_modules),
  RouterTools.ofLazy(
    AsmRouteId.internal_drp_sources_modules,
    () =>
      import('@asm/components-platform-administration/components/internal/drp-sources/asm-internal-drp-source-modules-page/asm-internal-drp-modules-page/asm-internal-drp-sources-modules-page.component').then(
        m => m.AsmInternalDrpSourcesModulesPageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_DRP_SOURCES],
  ),
  RouterTools.ofLazy(
    AsmRouteId.internal_drp_sources_sources,
    () =>
      import('@asm/components-platform-administration/components/internal/drp-sources/asm-internal-drp-source-sources-page/asm-internal-drp-sources-page/asm-internal-drp-sources-page.component').then(
        m => m.AsmInternalDrpSourcesPageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_DRP_SOURCES],
  ),
  RouterTools.ofLazy(
    AsmRouteId.internal_tasks,
    () =>
      import('@asm/components-engagements/components/internal/tasks/internal-tasks-page/internal-tasks-page.component').then(
        m => m.InternalTasksPageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_INTERNAL_TASKS],
  ),

  // Platform settings
  RouterTools.ofRedirect(AsmRouteId.admin_platform_settings, AsmRouteId.admin_settings_rules),
  RouterTools.ofLazy(
    AsmRouteId.admin_settings_definitions,
    () =>
      import('@asm/components-platform-administration/components/settings/asm-settings-definitions-page/asm-settings-definitions-page.component').then(
        m => m.AsmSettingsDefinitionsPageComponent,
      ),
    [AsmAuthority.MANAGE_GLOBAL_SETTINGS],
  ),
  RouterTools.ofLazy(
    AsmRouteId.admin_settings_rules,
    () =>
      import('@asm/components-platform-administration/components/settings/asm-settings-rules-list-page/asm-settings-rules-list-page.component').then(
        m => m.AsmSettingsRulesListPageComponent,
      ),
    [AsmAuthority.MANAGE_GLOBAL_SETTINGS],
  ),

  // Smoke Tests
  RouterTools.ofRedirect(AsmRouteId.admin_smoke_tests, AsmRouteId.admin_smoke_tests_suites),
  RouterTools.ofLazy(
    AsmRouteId.admin_smoke_tests_suites,
    () =>
      import('@asm/components-platform-administration/components/admin/asm-smoke-test-suites-page/asm-smoke-test-suites-page.component').then(
        m => m.AsmSmokeTestSuitesPageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_APP, AsmAuthority.FEATURE_SMOKE_TESTS],
  ),
  RouterTools.ofLazy(
    AsmRouteId.admin_smoke_tests_runs,
    () =>
      import('@asm/components-platform-administration/components/admin/asm-smoke-test-runs-page/asm-smoke-test-runs-page.component').then(
        m => m.AsmSmokeTestRunsPageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_APP, AsmAuthority.FEATURE_SMOKE_TESTS],
  ),
  RouterTools.ofLazy(
    AsmRouteId.admin_smoke_tests_run,
    () =>
      import('@asm/components-platform-administration/components/admin/asm-smoke-test-run-detail-page/asm-smoke-test-run-detail-page.component').then(
        m => m.AsmSmokeTestRunDetailPageComponent,
      ),
    [AsmAuthority.VIEW_ADMIN_APP, AsmAuthority.FEATURE_SMOKE_TESTS],
  ),

  // Dashboard definitions
  RouterTools.ofRedirect(AsmRouteId.resources, AsmRouteId.widgets),
  RouterTools.ofRedirect(AsmRouteId.dashboards, AsmRouteId.dashboards_overview),
  RouterTools.ofLazy(AsmRouteId.widgets, () =>
    import('@asm/components-dashboards/components/dashboard/asm-admin-widget-types-page/asm-admin-widget-types-page.component').then(
      m => m.AsmAdminWidgetTypesPageComponent,
    ),
  ),
  RouterTools.ofLazy(AsmRouteId.dashboards_definitions, () =>
    import('@asm/components-dashboards/components/dashboard/asm-admin-definitions-page/asm-admin-definitions-page.component').then(
      m => m.AsmAdminDefinitionsPageComponent,
    ),
  ),
  RouterTools.ofLazy(AsmRouteId.dashboards_overview, () =>
    import('@asm/components-platform-administration/components/dashboard/asm-admin-dashboards-page/asm-admin-dashboards-page.component').then(
      m => m.AsmAdminDashboardsPageComponent,
    ),
  ),
  {
    ...RouterTools.ofLazy(AsmRouteId.dashboards_detail, () =>
      import('@asm/components-platform-administration/components/dashboard/asm-admin-dashboard-detail-page/asm-admin-dashboard-detail-page.component').then(
        m => m.AsmAdminDashboardDetailPageComponent,
      ),
    ),
    canDeactivate: [solCanDeactivate],
  },
  RouterTools.ofLazy(AsmRouteId.dataSources, () =>
    import('@asm/components-dashboards/components/dashboard/asm-admin-data-sources-page/asm-admin-data-sources-page.component').then(
      m => m.AsmAdminDataSourcesPageComponent,
    ),
  ),

  // Banners
  RouterTools.ofLazy(
    AsmRouteId.banners,
    () =>
      import('@asm/components-platform-shell/components/banners/asm-banner-admin-page/asm-banner-admin-page.component').then(
        m => m.AsmBannerAdminPageComponent,
      ),
    [AsmAuthority.EDIT_ADMIN_BANNERS],
  ),
];
